import React from "react";
import {
  Autocomplete,
  InputAdornment,
  Paper,
  TextField,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

const SEARCH_ORANGE = "#F35A3A";

export function SearchPickerPaper(props) {
  const { sx, ...rest } = props;
  return (
    <Paper
      {...rest}
      elevation={10}
      sx={{
        mt: 0.65,
        borderRadius: 2.2,
        overflow: "hidden",
        border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.14)",
        bgcolor: "var(--wp-panel-surface, #fff)",
        boxShadow: "0 16px 40px rgba(0,0,0,.22)",
        "& .MuiAutocomplete-listbox": {
          p: 0.6,
          maxHeight: 304,
        },
        "& .MuiAutocomplete-option": {
          minHeight: 38,
          px: 1.15,
          py: 0.75,
          my: 0.2,
          borderRadius: 1.55,
          fontSize: ".9rem",
          lineHeight: 1.25,
          "&[aria-selected='true']": {
            bgcolor: "rgba(243,90,58,.10)",
          },
          "&.Mui-focused": {
            bgcolor: "rgba(243,90,58,.08)",
          },
          "&.Mui-focused[aria-selected='true']": {
            bgcolor: "rgba(243,90,58,.14)",
          },
        },
        ...sx,
      }}
    />
  );
}

export const searchPickerSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 42,
    borderRadius: 2.15,
    bgcolor: "var(--wp-panel-surface, #fff)",
    pr: "8px !important",
    transition: "border-color .16s ease, box-shadow .16s ease, transform .16s ease",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(243,90,58,.48)",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(243,90,58,.11)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: SEARCH_ORANGE,
      borderWidth: 1.5,
    },
  },
  "& .MuiInputBase-input": {
    py: "9px !important",
  },
  "& .MuiAutocomplete-endAdornment": {
    right: 5,
  },
};

export function SearchPicker({
  options = [],
  value = null,
  onChange,
  label,
  placeholder = "Buscar…",
  helperText = "",
  error = false,
  loading = false,
  loadingText = "Buscando…",
  noOptionsText = "Sin resultados",
  getOptionLabel,
  isOptionEqualToValue,
  renderOption,
  freeSolo = false,
  inputValue,
  onInputChange,
  disabled = false,
  disableClearable = false,
  filterOptions,
  maxVisibleOptions = 9,
  size = "small",
  fullWidth = true,
  sx,
  textFieldProps = {},
  ...autocompleteProps
}) {
  const effectiveFilterOptions = React.useCallback((available, state) => {
    const source = Array.isArray(available) ? available : [];
    const filtered = filterOptions
      ? filterOptions(source, state)
      : source.filter((option) => {
          const labelValue = getOptionLabel ? getOptionLabel(option) : String(option ?? "");
          const haystack = String(labelValue || "").toLocaleLowerCase("es");
          const needle = String(state?.inputValue || "").trim().toLocaleLowerCase("es");
          return !needle || haystack.includes(needle);
        });
    return filtered.slice(0, Math.max(1, maxVisibleOptions));
  }, [filterOptions, getOptionLabel, maxVisibleOptions]);

  return (
    <Autocomplete
      {...autocompleteProps}
      fullWidth={fullWidth}
      autoHighlight
      openOnFocus
      clearOnBlur={false}
      selectOnFocus
      handleHomeEndKeys
      size={size}
      disabled={disabled}
      freeSolo={freeSolo}
      disableClearable={disableClearable}
      options={Array.isArray(options) ? options : []}
      value={value}
      inputValue={inputValue}
      onInputChange={onInputChange}
      onChange={onChange}
      loading={loading}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderOption}
      filterOptions={effectiveFilterOptions}
      PaperComponent={SearchPickerPaper}
      ListboxProps={{ style: { maxHeight: 304, padding: 6 } }}
      sx={{ ...searchPickerSx, ...sx }}
      renderInput={(params) => {
        const providedInputProps = textFieldProps.InputProps || {};
        return (
          <TextField
            {...params}
            {...textFieldProps}
            label={label}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              ...providedInputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start" sx={{ ml: 0.1, mr: 0.2 }}>
                    <SearchRoundedIcon sx={{ fontSize: 19, color: error ? "error.main" : "text.secondary" }} />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                  {providedInputProps.startAdornment}
                </>
              ),
            }}
          />
        );
      }}
    />
  );
}

export default SearchPicker;
